import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faSpinner, faArrowRight, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import gsap from 'gsap';
import { authAPI } from '../services/api';
import Navbar from './Navbar';
import logo from '../assets/autoboy_logo2.png';
import styles from './LoginSignup.module.css'; // Reuse styles for consistency

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  // If no token but email present, user just registered and needs to check their email
  const [status, setStatus] = useState(token ? 'verifying' : 'pending'); // 'pending', 'verifying', 'success', 'error'
  const [message, setMessage] = useState(
    token ? 'Please wait while we verify your email...' : `We sent a verification link to ${email || 'your email'}. Please check your inbox and click the link to activate your account.`
  );
  const [errorDetails, setErrorDetails] = useState('');

  const cardRef = useRef(null);
  const iconRef = useRef(null);

  useEffect(() => {
    // GSAP Entrance Animation
    gsap.from(cardRef.current, {
      opacity: 0,
      y: 30,
      duration: 0.8,
      ease: 'power3.out'
    });

    if (!token) {
      // No token means user just registered — stay on pending state
      return;
    }

    const verifyToken = async () => {
      try {
        // Call backend API (relative to baseURL /api/v1)
        const response = await authAPI.verifyEmail(token);
        
        // Handle success
        setStatus('success');
        setMessage('Your email has been successfully verified! Welcome to AutoboyExpress.');
        
        // If the backend returns user data and tokens, store them
        const data = response.data || response;
        if (data.token) {
          localStorage.setItem('authToken', data.token);
          if (data.refresh_token) {
            localStorage.setItem('refreshToken', data.refresh_token);
          }
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
        }

        // Animated success icon
        gsap.from(iconRef.current, {
          scale: 0.5,
          opacity: 0,
          duration: 0.5,
          ease: 'back.out(1.7)'
        });

      } catch (error) {
        setStatus('error');
        setMessage('Verification failed');
        setErrorDetails(error.message || 'The verification link may have expired or is invalid.');
        
        // Animated error icon
        gsap.from(iconRef.current, {
          x: 10,
          repeat: 5,
          yoyo: true,
          duration: 0.1,
          ease: 'power1.inOut'
        });
      }
    };

    verifyToken();
  }, [token]);

  const handleContinue = () => {
    if (status === 'success') {
      navigate('/homepage');
    } else {
      navigate('/auth');
    }
  };

  return (
    <>
      <Navbar darkMode={false} />
      <div className={styles.authContainer} style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div ref={cardRef} style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '3rem',
          width: '100%',
          maxWidth: '500px',
          textAlign: 'center',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
          {/* Logo */}
          <div style={{ marginBottom: '2rem' }}>
            <img src={logo} alt="AutoboyExpress Logo" style={{ height: '40px', objectFit: 'contain' }} />
          </div>

          {/* Status Icon */}
          <div ref={iconRef} style={{ marginBottom: '2rem', fontSize: '4rem' }}>
            {status === 'verifying' && <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#22C55E' }} />}
            {status === 'pending' && <FontAwesomeIcon icon={faEnvelopeOpenText} style={{ color: '#3B82F6' }} />}
            {status === 'success' && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22C55E' }} />}
            {status === 'error' && <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#EF4444' }} />}
          </div>

          <h2 style={{ fontSize: '1.875rem', fontWeight: '800', marginBottom: '1rem', color: '#111827' }}>
            {status === 'verifying' ? 'Verifying Email' : status === 'pending' ? 'Check Your Email' : status === 'success' ? 'Verification Success' : 'Verification Failed'}
          </h2>

          <p style={{ color: '#4B5563', fontSize: '1.125rem', marginBottom: '2rem', lineHeight: '1.5' }}>
            {message}
          </p>

          {errorDetails && (
            <p style={{ color: '#EF4444', fontSize: '0.9rem', marginBottom: '2rem', background: '#FEF2F2', padding: '1rem', borderRadius: '12px' }}>
              {errorDetails}
            </p>
          )}

          {status !== 'verifying' && (
            <button
              onClick={handleContinue}
              className={styles.submitBtn}
              style={{ padding: '1rem 2rem', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', margin: '0 auto' }}
            >
              <span>{status === 'success' ? 'Go to Home' : status === 'pending' ? 'Back to Login' : 'Back to Login'}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          )}

          {status === 'success' && (
            <div style={{ marginTop: '2rem', color: '#6B7280', fontSize: '0.875rem' }}>
              <FontAwesomeIcon icon={faEnvelopeOpenText} style={{ marginRight: '0.5rem' }} />
              You can now enjoy all AutoboyExpress features.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
